import React, { useCallback, useEffect, useState } from 'react'
import styles from 'components/Contractor/ContractorView.module.css'
import { ReactComponent as LoadingIcon } from 'assets/icons/loading.svg'
import { ReactComponent as UnloadingIcon } from 'assets/icons/unloading.svg'
import { ReactComponent as CarIcon } from 'assets/icons/car-red.svg'
import {
  downloadFileByOrderToken,
  fetchIsOrderRatedByClient,
  getOrderAttachmentByToken,
  rateOrderByClient
} from 'services'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import header from 'assets/images/contractor-view-header.jpg'
import { ContainedButton } from 'components/Shared/ContainedButton'
import { FileChip } from 'components/Shared/FileChip'
import { ContractorOrderStatus } from 'components/Contractor/ContractorOrderStatus'
import { fetchOrderByToken } from 'services/OrderService'
import { HTTP_404_ERROR, HTTP_422_ERROR, HTTP_500_ERROR } from 'Constants'
import { useErrorHandling } from 'context/errorHandling'
import { FilesPreviewDialog } from 'components/Shared/FilesPreviewDialog'
import { ClientOrderRatingsDialog } from 'components/OrderRating'
import ApiError from 'constants/ApiError'

export function ContractorView (props) {
  const [order, setOrder] = useState(null)
  const tokenString = props.match.params.orderToken
  const { t, i18n } = useTranslation()
  const [previewedAttachment, setPreviewedAttachment] = useState(null)
  const [isOrderRatedByClient, setIsOrderRatedByClient] = useState(null)
  const [isRatingsDialogOpen, setIsRatingsDialogOpen] = useState(false)

  const { handleError } = useErrorHandling()

  const isViewedByForwarder = new URLSearchParams(props.location.search).get('rt')
  const isOrderFinished = useCallback(() => order?.progressBar?.every(point => point.status === 'DONE' || point.status === 'LAST_COMPLETED'), [order])

  useEffect(
    () => {
      fetchOrderByToken(tokenString).then(order => {
        setOrder(order)
        //Local language change, does not override localstorage value
        i18n.changeLanguage(order.language.toLowerCase())
      })
    },
    [tokenString, i18n])

  useEffect(() => {
    if (isOrderRatedByClient === null && isOrderFinished()) {
      fetchIsOrderRatedByClient(tokenString)
        .then(({ isRatedByClient }) => {
          setIsOrderRatedByClient(isRatedByClient)
          if (isViewedByForwarder === null) {
            setIsRatingsDialogOpen(!isRatedByClient)
          }
        })
        .catch(handleError)
    }
  }, [tokenString, isOrderRatedByClient, isViewedByForwarder, isOrderFinished, handleError])

  function handleClientSubmit (values) {
    if (values) {
      rateOrderByClient(tokenString, { ...values })
        .then(() => setIsOrderRatedByClient(null))
        .catch(error => handleError(error, {
          [HTTP_422_ERROR]: error?.response?.data?.errorCode === 'ORDER_ALREADY_RATED' ?
            'notifications.error.orderAlreadyRated' : ApiError.UnprocessableEntity
        }))
    }
    setIsRatingsDialogOpen(false)
  }

  const onDownloadAttachmentByOrderToken = (attachment, token) => {
    downloadFileByOrderToken(attachment, token)
      .catch(error => handleError(error, {
        [HTTP_404_ERROR]: 'notifications.error.fileNotFound',
        [HTTP_500_ERROR]: 'notifications.error.fileDownloadError'
      }))
  }

  return (order && <div className={styles.background}>
      <div className={styles.contractorViewWrapper}>
        <div className={styles.headerPhotoContainer}>
          <img src={header} alt="header"/>
        </div>
        <div className={styles.contentContainer}>
          <ClientOrderRatingsDialog onSubmit={handleClientSubmit}
                                    isOpen={isRatingsDialogOpen}/>
          <div
            className={classNames(styles.header, (order.referenceId && order.referenceId.length > 14) && styles.reduceHeaderPadding)}>
            <h1>
              {`${t('contractorView.title')} ${order.readableId}`}
              {order.referenceId && ` / ${order.referenceId}`}
            </h1>
          </div>
          <div className={styles.subHeader}>
            <h3>{t('contractorView.orderDetails')}</h3>
          </div>
          <div className={styles.orderDetails}>
            <div className={styles.orderDetailsLoadings}>
              <div className={styles.textWithIcon}>
                <LoadingIcon/>
                <span>{t(
                  'contractorView.loading')} {`${order.firstLoadingLocation.country} ${order.firstLoadingLocation.zipCode}`}</span>
              </div>
              <div className={styles.textWithIcon}>
                <UnloadingIcon/>
                <span>{t(
                  'contractorView.unloading')} {`${order.lastUnloadingLocation.country} ${order.lastUnloadingLocation.zipCode}`}</span>
              </div>
            </div>
            <div className={styles.textWithIcon}>
              <CarIcon className={styles.carIcon}/>
              <span>{t(
                'contractorView.carRegistration')} {order.carRegistrationNumber}</span>
            </div>
          </div>
          <div className={styles.subHeader}>
            <h3>{t('contractorView.status')}</h3>
          </div>
          <ContractorOrderStatus orderStatuses={order.progressBar}/>
          {order.attachments.length !== 0 ?
            <>
              <div className={styles.subHeader}>
                <h3>{t('contractorView.downloadFiles')}</h3>
              </div>
              <div className={styles.attachments}>
                <FilesPreviewDialog open={!!previewedAttachment}
                                    fileToShow={previewedAttachment}
                                    filesSource={attachment => getOrderAttachmentByToken(attachment, tokenString)}
                                    files={order.attachments}
                                    onFileChanged={setPreviewedAttachment}
                                    onClose={() => setPreviewedAttachment(null)}/>
                <ul>
                  {order.attachments.map(attachment =>
                    <FileChip
                      key={attachment.filename}
                      file={attachment}
                      onDownload={() => onDownloadAttachmentByOrderToken(attachment, tokenString)}
                      onShow={() => setPreviewedAttachment(attachment)}
                      classes={{ nameContainer: styles.attachmentFilename }}/>
                  )}
                </ul>
              </div>
            </>
            :
            ' '
          }
        </div>
        <div className={styles.footerContainer}>
          <div className={styles.questionsWrapper}>
            <div className={styles.questionsTitle}>
              <p>{t('contractorView.questionsTitle')}</p>
              <p>{t('contractorView.questionsSubTitle')}</p>
            </div>
            <div className={`${styles.orderCaretakersContainer} ${!order.dispatcher ? styles.reverseRow : ''}`}>
              <div className={styles.caretaker}>
                {order.dispatcher && <>
                  <p>{t('contractorView.dispatcher')}</p>
                  <p>{`${order.dispatcher.firstName} ${order.dispatcher.lastName}`}</p>
                  <a
                    href={`tel:${order.dispatcher.phoneNumber}`}>{order.dispatcher.phoneNumber}</a>
                  <a
                    href={`mailto:${order.dispatcher.email}`}>{order.dispatcher.email}</a>
                </>}
              </div>
              <div className={styles.caretaker}>
                <p>{t('contractorView.forwarder')}</p>
                <p>{`${order.forwarder.firstName} ${order.forwarder.lastName}`}</p>
                <a
                  href={`tel:${order.forwarder.phoneNumber}`}>{order.forwarder.phoneNumber}</a>
                <a
                  href={`mailto:${order.forwarder.email}`}>{order.forwarder.email}</a>
              </div>
            </div>
          </div>
          <div className={styles.satisfied}>
            <p>{t('contractorView.newOrderSectionTitle')}</p>
            <ContainedButton className={styles.button}
                             label={'contractorView.newOrderSectionButtonLabel'}
                             onClick={() => {
                               window.location.href = `mailto:${order.forwarder.email}`
                             }}/>
            {!isOrderRatedByClient && isViewedByForwarder === null && isOrderFinished() &&
              <ContainedButton className={styles.button}
                               label={'contractorView.rateOrder'}
                               onClick={() => setIsRatingsDialogOpen(true)}/>
            }
          </div>
        </div>
      </div>
    </div>
  )
}




